import { EmployeeId } from '@modernloop/shared/ids';
import { uniq } from 'lodash';

import { AdditionalAttendeeInput, DynamicAudience } from 'src/generated/mloop-graphql';

import { SettingAttendeeEntry, SettingAttendeeKind } from 'src/utils/settingAdditionalAttendees';

/**
 * Per-recipient additional-attendee model for interviewer events.
 *
 * One interview owns one {@link InterviewAdditionalAttendees}. Every recipient is a concrete
 * entry that carries its own optional/required role, so one invite can mix roles. Dynamic
 * audiences are resolved on the communications page and submitted as concrete member entries
 * that remember which audience produced them (`sourceAudience`), which is what lets the UI badge
 * them and re-offer the audience chip on a later edit.
 */
export enum AdditionalAttendeeKind {
  Employee = 'EMPLOYEE',
  Email = 'EMAIL',
}

export type AdditionalAttendeeEntry =
  | {
      kind: AdditionalAttendeeKind.Employee;
      employeeId: string;
      isOptional: boolean;
      /** Set when this member came from resolving a dynamic audience. */
      sourceAudience?: DynamicAudience;
    }
  | {
      kind: AdditionalAttendeeKind.Email;
      email: string;
      isOptional: boolean;
      sourceAudience?: DynamicAudience;
    };

/**
 * An audience chip on an event. `isOptional` is the role its members inherit when they are
 * materialized: a settings-seeded audience carries the flag configured for it, and an audience a
 * user picks here starts with the new-recipient default like every other manual pick.
 */
export type SelectedAudience = {
  audience: DynamicAudience;
  isOptional: boolean;
};

/** One person kept out of one audience's contribution. */
export type DismissedAudienceMember = {
  audience: DynamicAudience;
  attendeeKey: string;
};

export type InterviewAdditionalAttendees = {
  /** Audience chips. Their members live in `attendees` as concrete entries. */
  dynamicAudiences: SelectedAudience[];
  attendees: AdditionalAttendeeEntry[];
  /**
   * Members the scheduler removed from a specific audience's contribution, by deleting that
   * member's chip. A selected audience keeps offering its members on every pass, so without this a
   * deleted member would be put straight back and could never be dropped.
   *
   * Keyed by (audience, person) rather than by person alone: removing someone's EXPLICIT chip must
   * still let a selected audience claim them as its own member, and a person can belong to two
   * audiences independently.
   */
  dismissedAudienceMembers: DismissedAudienceMember[];
  /**
   * Audiences reconstructed from a STORED snapshot, whose membership has not been compared against
   * that snapshot yet.
   *
   * A stored list was already reconciled when it was saved, so a member of one of these audiences
   * who is absent from the entries was removed on purpose. The first materialization pass -- the
   * only place with the resolved membership -- turns that absence into concrete dismissals and
   * empties this list. Without it, a member deleted before a save comes back on the next reload.
   */
  snapshotAudiences: DynamicAudience[];
};

/** Keyed by the interview's `event.id` (the application-stage interview id). */
export type AdditionalAttendeesByInterviewId = { [interviewId: string]: InterviewAdditionalAttendees };

export const emptyInterviewAdditionalAttendees = (): InterviewAdditionalAttendees => ({
  dynamicAudiences: [],
  attendees: [],
  dismissedAudienceMembers: [],
  snapshotAudiences: [],
});

/** Stable identity for a recipient. Provenance is deliberately excluded: one person, one row. */
export const additionalAttendeeKey = (entry: AdditionalAttendeeEntry): string =>
  entry.kind === AdditionalAttendeeKind.Employee
    ? `employee:${entry.employeeId}`
    : `email:${entry.email.trim().toLowerCase()}`;

const isFromAudience = (entry: AdditionalAttendeeEntry): boolean => Boolean(entry.sourceAudience);

const employeeChipKey = (employeeId: string): string => `employee:${employeeId}`;

export const getExplicitEmployeeIds = (value: InterviewAdditionalAttendees | undefined): EmployeeId[] =>
  (value?.attendees ?? [])
    .filter((entry) => entry.kind === AdditionalAttendeeKind.Employee && !isFromAudience(entry))
    .map((entry) => (entry as { employeeId: string }).employeeId as EmployeeId);

export const getExplicitEmails = (value: InterviewAdditionalAttendees | undefined): string[] =>
  (value?.attendees ?? [])
    .filter((entry) => entry.kind === AdditionalAttendeeKind.Email && !isFromAudience(entry))
    .map((entry) => (entry as { email: string }).email);

export const isEmptyInterviewAdditionalAttendees = (value: InterviewAdditionalAttendees | undefined): boolean =>
  !value || (value.attendees.length === 0 && value.dynamicAudiences.length === 0);

/**
 * Seeds an event from the resolved org/job-stage setting entries. Each configured recipient keeps
 * the role it was configured with, and a configured audience becomes an audience chip whose
 * members will inherit that audience's role when they are materialized.
 */
export const seedFromSettingEntries = (entries: SettingAttendeeEntry[]): InterviewAdditionalAttendees => {
  const attendees: AdditionalAttendeeEntry[] = [];
  const dynamicAudiences: SelectedAudience[] = [];

  entries.forEach((entry) => {
    switch (entry.kind) {
      case SettingAttendeeKind.Employee:
        attendees.push({
          kind: AdditionalAttendeeKind.Employee,
          employeeId: entry.employeeId,
          isOptional: entry.isOptional,
        });
        break;
      case SettingAttendeeKind.Email:
        attendees.push({ kind: AdditionalAttendeeKind.Email, email: entry.email, isOptional: entry.isOptional });
        break;
      default:
        dynamicAudiences.push({ audience: entry.audience, isOptional: entry.isOptional });
    }
  });

  return { dynamicAudiences, attendees, dismissedAudienceMembers: [], snapshotAudiences: [] };
};

type AdditionalAttendeesSnapshot =
  | {
      attendees?:
        | {
            email?: string | null;
            employeeId?: string | null;
            isOptional: boolean;
            sourceAudience?: DynamicAudience | null;
          }[]
        | null;
    }
  | null
  | undefined;

/**
 * Reads one stored `additionalAttendeesV2` snapshot. The backend always populates `attendees`
 * (rows written before per-recipient roles existed answer with entries materialized from the
 * legacy fields), so the collection-level `isOptional` is deliberately ignored here.
 */
export const fromAdditionalAttendeesSnapshot = (
  snapshot: AdditionalAttendeesSnapshot
): InterviewAdditionalAttendees | null => {
  if (!snapshot?.attendees?.length) {
    return null;
  }

  const attendees: AdditionalAttendeeEntry[] = [];
  snapshot.attendees.forEach((attendee) => {
    const sourceAudience = attendee.sourceAudience ?? undefined;
    if (attendee.employeeId) {
      attendees.push({
        kind: AdditionalAttendeeKind.Employee,
        employeeId: attendee.employeeId,
        isOptional: Boolean(attendee.isOptional),
        sourceAudience,
      });
      return;
    }
    if (attendee.email) {
      attendees.push({
        kind: AdditionalAttendeeKind.Email,
        email: attendee.email,
        isOptional: Boolean(attendee.isOptional),
        sourceAudience,
      });
    }
  });

  if (!attendees.length) {
    return null;
  }

  const sourceAudiences = uniq(attendees.map((entry) => entry.sourceAudience).filter(Boolean) as DynamicAudience[]);

  return {
    dynamicAudiences: sourceAudiences.map((audience) => ({ audience, isOptional: false })),
    attendees,
    dismissedAudienceMembers: [],
    // These audiences were already reconciled when the snapshot was saved. Materialization turns
    // whoever is missing from the entries into a dismissal, so a member deleted before the save
    // does not come back on this reload.
    snapshotAudiences: sourceAudiences,
  };
};

type SelectorChange = {
  current: InterviewAdditionalAttendees | undefined;
  selectedEmployeeIds: string[];
  selectedEmailAddresses: string[];
  dynamicAudiences: DynamicAudience[];
  /**
   * The role a recipient this change introduces starts with. `true` -- optional -- is the
   * per-recipient contract; the caller states it because only the caller knows whether that
   * contract is in force (see `useAdditionalAttendeeChips`).
   */
  newEntriesOptional: boolean;
};

/**
 * Rebuilds an event's recipient list after a selector change -- adding or removing a chip.
 *
 * Roles already chosen survive; a newly picked recipient starts with `newEntriesOptional`, which
 * the per-recipient contract sets to optional: a manual pick says who to invite, and an addition
 * nobody has to attend should not oblige them. The settings default still speaks only for the
 * recipients it configured. An audience keeps contributing its members only while its chip is
 * selected, and a person picked explicitly always claims their own entry, so removing the audience
 * never removes them.
 */
export const applySelectorChange = ({
  current,
  selectedEmployeeIds,
  selectedEmailAddresses,
  dynamicAudiences,
  newEntriesOptional,
}: SelectorChange): InterviewAdditionalAttendees => {
  const previousByKey = new Map((current?.attendees ?? []).map((entry) => [additionalAttendeeKey(entry), entry]));
  const previousAudiences = new Map((current?.dynamicAudiences ?? []).map((entry) => [entry.audience, entry]));
  const attendees: AdditionalAttendeeEntry[] = [];
  const claimed = new Set<string>();

  const roleFor = (key: string): boolean => previousByKey.get(key)?.isOptional ?? newEntriesOptional;

  uniq(selectedEmployeeIds).forEach((employeeId) => {
    const key = `employee:${employeeId}`;
    claimed.add(key);
    attendees.push({ kind: AdditionalAttendeeKind.Employee, employeeId, isOptional: roleFor(key) });
  });

  uniq(selectedEmailAddresses).forEach((email) => {
    const key = `email:${email.trim().toLowerCase()}`;
    if (claimed.has(key)) {
      return;
    }
    claimed.add(key);
    attendees.push({ kind: AdditionalAttendeeKind.Email, email, isOptional: roleFor(key) });
  });

  const audiences = uniq(dynamicAudiences).map(
    (audience) => previousAudiences.get(audience) ?? { audience, isOptional: newEntriesOptional }
  );
  const audienceNames = audiences.map((entry) => entry.audience);

  // Members an audience already contributed stay, with their own roles; the audience is not
  // re-materialized here, so a member the user dropped stays dropped.
  (current?.attendees ?? []).forEach((entry) => {
    const key = additionalAttendeeKey(entry);
    if (!entry.sourceAudience || claimed.has(key) || !audienceNames.includes(entry.sourceAudience)) {
      return;
    }
    claimed.add(key);
    attendees.push(entry);
  });

  return {
    dynamicAudiences: audiences,
    attendees,
    // A person whose explicit chip was just removed is no longer dismissed: if a selected audience
    // also names them, it may now claim them as its own member.
    // A dismissal survives while its audience is still selected. Filtering on `claimed` -- as an
    // earlier version did -- discarded exactly the dismissals that matter, because a dismissed
    // person is by definition not on the invite, and the next pass re-invited them.
    dismissedAudienceMembers: (current?.dismissedAudienceMembers ?? []).filter((dismissal) =>
      audienceNames.includes(dismissal.audience)
    ),
    // De-selecting an audience forgets everything about it, so re-selecting it is a fresh
    // statement of intent that brings its whole membership back.
    snapshotAudiences: (current?.snapshotAudiences ?? []).filter((audience) => audienceNames.includes(audience)),
  };
};

/**
 * Removes exactly one recipient by deleting their audience-member chip. The audience stays
 * selected, so the removal is recorded -- otherwise the next materialization pass would put them
 * straight back.
 */
export const removeAdditionalAttendee = (
  value: InterviewAdditionalAttendees,
  attendeeKey: string,
  resolveAudienceMembers: (audience: DynamicAudience) => string[]
): InterviewAdditionalAttendees => {
  const removed = value.attendees.filter((entry) => additionalAttendeeKey(entry) === attendeeKey);
  // Only an audience-owned entry needs a dismissal; an explicit pick simply leaves the list, and a
  // selected audience may then claim that person as its own member.
  const isAudienceOwned = removed.some((entry) => Boolean(entry.sourceAudience));

  // The row's own audience always counts, whether or not a membership query has answered yet -- the
  // entry already names it. On top of that, every other selected audience that resolves to this
  // person is dismissed too, because one row stands for the person and a second audience naming
  // them would otherwise re-add them on the next pass.
  const dismissedAudiences = new Set<DynamicAudience>(
    removed.map((entry) => entry.sourceAudience).filter((audience): audience is DynamicAudience => Boolean(audience))
  );
  if (isAudienceOwned) {
    value.dynamicAudiences
      .filter(({ audience }) => resolveAudienceMembers(audience).some((id) => employeeChipKey(id) === attendeeKey))
      .forEach(({ audience }) => dismissedAudiences.add(audience));
  }
  const dismissals = [...dismissedAudiences].map((audience) => ({ audience, attendeeKey }));

  const alreadyDismissed = new Set(
    (value.dismissedAudienceMembers ?? []).map((entry) => `${entry.audience}|${entry.attendeeKey}`)
  );

  return {
    ...value,
    attendees: value.attendees.filter((entry) => additionalAttendeeKey(entry) !== attendeeKey),
    dismissedAudienceMembers: [
      ...(value.dismissedAudienceMembers ?? []),
      ...dismissals.filter((entry) => !alreadyDismissed.has(`${entry.audience}|${entry.attendeeKey}`)),
    ],
  };
};

/**
 * Adds the members of a selected audience that are not represented yet, and nothing else.
 *
 * Audience membership arrives asynchronously, so a list can hold an audience whose members are not
 * entries yet: the org/job-stage default seeds audience chips without members, and a user can pick
 * an audience before its query answers. Both are fixed by calling this again once the membership
 * is known.
 *
 * Deliberately additive. Running `applySelectorChange` instead would drop every member of an
 * audience whose query has not answered yet, which is exactly the state this repairs. Returns the
 * SAME object when there is nothing to add, so a caller can reconcile on every render without
 * looping.
 */
export const materializeAudienceMembers = (
  value: InterviewAdditionalAttendees,
  resolveAudienceMembers: (audience: DynamicAudience) => string[]
): InterviewAdditionalAttendees => {
  const claimed = new Set(value.attendees.map(additionalAttendeeKey));
  const dismissedKey = (audience: DynamicAudience, attendeeKey: string) => `${audience}|${attendeeKey}`;
  const dismissed = new Set(
    (value.dismissedAudienceMembers ?? []).map((entry) => dismissedKey(entry.audience, entry.attendeeKey))
  );
  const added: AdditionalAttendeeEntry[] = [];
  const newDismissals: DismissedAudienceMember[] = [];
  const reconciledSnapshotAudiences: DynamicAudience[] = [];

  // Step 1: a stored snapshot was already reconciled when it was saved, so anyone its audiences
  // name who is missing from its entries was removed on purpose. This is the only place with the
  // resolved membership, so the absence is turned into a dismissal here. An audience whose query
  // has not answered stays pending, and a later answer still reconciles it.
  (value.snapshotAudiences ?? []).forEach((audience) => {
    const members = resolveAudienceMembers(audience);
    if (!members.length) {
      return;
    }
    reconciledSnapshotAudiences.push(audience);
    members.forEach((employeeId) => {
      const key = employeeChipKey(employeeId);
      if (claimed.has(key) || dismissed.has(dismissedKey(audience, key))) {
        return;
      }
      dismissed.add(dismissedKey(audience, key));
      newDismissals.push({ audience, attendeeKey: key });
    });
  });

  // Step 2: every selected audience offers its members, minus whoever is already on the invite and
  // whoever was dismissed from that audience.
  value.dynamicAudiences.forEach(({ audience, isOptional }) => {
    resolveAudienceMembers(audience).forEach((employeeId) => {
      const key = employeeChipKey(employeeId);
      if (claimed.has(key) || dismissed.has(dismissedKey(audience, key))) {
        return;
      }
      claimed.add(key);
      // Members inherit the audience's configured role and become individually toggleable here.
      added.push({ kind: AdditionalAttendeeKind.Employee, employeeId, isOptional, sourceAudience: audience });
    });
  });

  // Same object when nothing changed, so a caller can reconcile on every render without looping.
  if (!added.length && !newDismissals.length && !reconciledSnapshotAudiences.length) {
    return value;
  }

  return {
    ...value,
    attendees: added.length ? [...value.attendees, ...added] : value.attendees,
    dismissedAudienceMembers: newDismissals.length
      ? [...(value.dismissedAudienceMembers ?? []), ...newDismissals]
      : (value.dismissedAudienceMembers ?? []),
    snapshotAudiences: (value.snapshotAudiences ?? []).filter(
      (audience) => !reconciledSnapshotAudiences.includes(audience)
    ),
  };
};

/**
 * The selector's view of an event's recipients: explicit picks become ordinary chips, audience
 * members become their own chips that remember which audience owns them, and audiences keep their
 * chip. Every recipient is visible, and each chip can be removed on its own.
 */
export type AdditionalAttendeeSelectorValue = {
  employeeIds: EmployeeId[];
  externalEmailAddresses: string[];
  dynamicAudiences: DynamicAudience[];
  audienceMembers: { employeeId: EmployeeId; sourceAudience: DynamicAudience }[];
};

export const toSelectorValue = (value: InterviewAdditionalAttendees | undefined): AdditionalAttendeeSelectorValue => ({
  employeeIds: getExplicitEmployeeIds(value),
  externalEmailAddresses: getExplicitEmails(value),
  dynamicAudiences: (value?.dynamicAudiences ?? []).map((entry) => entry.audience),
  audienceMembers: (value?.attendees ?? [])
    .filter((entry) => entry.kind === AdditionalAttendeeKind.Employee && Boolean(entry.sourceAudience))
    .map((entry) => ({
      employeeId: (entry as { employeeId: string }).employeeId as EmployeeId,
      sourceAudience: entry.sourceAudience as DynamicAudience,
    })),
});

export const setAdditionalAttendeeOptional = (
  value: InterviewAdditionalAttendees | undefined,
  attendeeKey: string,
  isOptional: boolean
): InterviewAdditionalAttendees => {
  const base = value ?? emptyInterviewAdditionalAttendees();
  return {
    ...base,
    attendees: base.attendees.map((entry) =>
      additionalAttendeeKey(entry) === attendeeKey ? { ...entry, isOptional } : entry
    ),
  };
};

/** GraphQL `AdditionalAttendeeInput[]` for one interviewer content on create/reschedule. */
export const toAdditionalAttendeeInputs = (
  value: InterviewAdditionalAttendees | undefined
): AdditionalAttendeeInput[] =>
  (value?.attendees ?? []).map((entry) =>
    entry.kind === AdditionalAttendeeKind.Employee
      ? { employeeId: entry.employeeId, isOptional: entry.isOptional, sourceAudience: entry.sourceAudience }
      : { email: entry.email, isOptional: entry.isOptional, sourceAudience: entry.sourceAudience }
  );

/** Proto-JSON `AdditionalAttendees` message for one interviewer content on the REST update. */
export type AdditionalAttendeesMessage = {
  attendees: {
    email?: string;
    employeeId?: string;
    isOptional: boolean;
    sourceAudience?: DynamicAudience;
  }[];
};

export const toAdditionalAttendeesMessage = (
  value: InterviewAdditionalAttendees | undefined
): AdditionalAttendeesMessage => ({
  attendees: toAdditionalAttendeeInputs(value) as AdditionalAttendeesMessage['attendees'],
});

/**
 * True when two lists name the same recipients with the same roles. The update payload omits a
 * row's `additionalAttendees` when this holds against the stored snapshot, which is what makes
 * the backend preserve that row untouched.
 *
 * `dynamicAudiences` is deliberately not compared. Only entries are stored and only entries are
 * sent, so an audience chip that resolves to nobody new changes neither what is on the invite nor
 * what the row will hold. Comparing it would make every such chip send a payload identical to the
 * stored one.
 */
export const isSameAdditionalAttendees = (
  left: InterviewAdditionalAttendees | undefined,
  right: InterviewAdditionalAttendees | undefined
): boolean => {
  const fingerprint = (value: InterviewAdditionalAttendees | undefined) =>
    (value?.attendees ?? [])
      .map((entry) => `${additionalAttendeeKey(entry)}|${entry.isOptional}|${entry.sourceAudience ?? ''}`)
      .sort()
      .join(',');

  return fingerprint(left) === fingerprint(right);
};

/**
 * Collapses every interview's list into the one schedule-level list the legacy request fields
 * carry. Used only while the per-interview flag is off. `isOptional` is true only when EVERY
 * recipient is optional: one boolean cannot express a mixed invite, and defaulting to true would
 * demote a required attendee.
 */
export const mergeForScheduleLevelRequest = (
  values: InterviewAdditionalAttendees[]
): { employeeIds: string[]; emails: string[]; dynamicAudiences: DynamicAudience[]; isOptional: boolean } => {
  const entries = values.flatMap((value) => value.attendees);
  const explicit = entries.filter((entry) => !isFromAudience(entry));

  return {
    employeeIds: uniq(
      explicit
        .filter((entry) => entry.kind === AdditionalAttendeeKind.Employee)
        .map((entry) => (entry as { employeeId: string }).employeeId)
    ),
    emails: uniq(
      explicit
        .filter((entry) => entry.kind === AdditionalAttendeeKind.Email)
        .map((entry) => (entry as { email: string }).email)
    ),
    dynamicAudiences: uniq(values.flatMap((value) => value.dynamicAudiences.map((entry) => entry.audience))),
    isOptional: entries.length > 0 && entries.every((entry) => entry.isOptional),
  };
};

/**
 * The seed for one event's recipients, with audiences already materialized.
 *
 * The order is the rule every surface follows: an existing draft wins (a seeding effect can re-run
 * and must not overwrite what the scheduler typed), then the event's stored list, then the
 * configured settings. `storedSnapshot` distinguishes an event that stored an EMPTY list -- pass
 * `emptyInterviewAdditionalAttendees()` -- from one that never stored anything, which is
 * `undefined`; without that distinction a deliberately cleared list is re-seeded from settings.
 */
export const buildEventAttendeesSeed = ({
  existingDraft,
  storedSnapshot,
  settingEntries,
  resolveAudienceMembers,
}: {
  existingDraft: InterviewAdditionalAttendees | undefined;
  storedSnapshot: InterviewAdditionalAttendees | undefined;
  settingEntries: SettingAttendeeEntry[];
  resolveAudienceMembers: (audience: DynamicAudience) => string[];
}): InterviewAdditionalAttendees =>
  materializeAudienceMembers(
    existingDraft ?? storedSnapshot ?? seedFromSettingEntries(settingEntries),
    resolveAudienceMembers
  );
